import { createTheme } from '@mui/material/styles';

/**
 * Phase 1 of the design-system plan: write the values down.
 *
 * Every number below was measured off the running app in a browser, not
 * invented — three audit passes across 23 routes at five widths. The point of
 * this file is that there is now ONE place to read them from.
 *
 * What this phase deliberately does NOT do: wire any of it in. `tokens` is a
 * plain object that nothing consumes yet. Only the theme keys that were
 * already live (palette.primary, palette.secondary, palette.surface and the
 * gold button override) stay wired, with their values untouched. That is what
 * keeps the promise that Phase 1 changes nothing on screen.
 *
 * Wiring happens later and on purpose:
 *   Phase 2 — emit `tokens` as CSS custom properties so the 40 hand-written
 *             stylesheets can read the same values, and add the lint rule.
 *   Phase 4 — adopt the type scale, spacing, radii and states in the shared
 *             components. That is where rendering legitimately changes.
 *
 * If you are adding a colour, a size or a duration: put it here first. If what
 * you need is already here under a different name, use that name instead.
 */

/** The four typefaces actually in use, with real fallbacks. */
const family = {
  // Display. Marketing headlines, and section titles in the product.
  serif: '"Source Serif 4", Cambria, Georgia, serif',
  // UI and body copy.
  //
  // These stacks are transcribed EXACTLY as the stylesheets had them, down to
  // the fallbacks. Adding "Helvetica Neue", Arial to `sans` looks like a free
  // improvement and is not: it changes which glyphs render in the window
  // before Inter finishes loading, which is a real visual difference on
  // text-heavy pages. Widening a fallback stack is a deliberate change with
  // its own before-and-after, not something to slip into a transcription.
  sans: '"Inter", -apple-system, sans-serif',
  // Labels, figures, code, anything that should line up in a column.
  mono: '"IBM Plex Mono", ui-monospace, monospace',
  // What MUI renders with today, until Phase 4 moves components onto `sans`.
  ui: 'Roboto, "Helvetica Neue", Arial, sans-serif',
  // Decorative only — the signature on the About page. Loaded in index.html.
  script: '"Alex Brush", cursive',
};

/**
 * The design tokens.
 *
 * Counts in the comments are what the audit found in use, so the size of each
 * reduction is visible. Nothing here is a new colour: every value already
 * appears somewhere in the product today.
 */
export const tokens = {
  /** 9 values, down from 470 hex values and 18 purples. */
  color: {
    // The brand purple. Confirmed by the client: RGB 65 / 59 / 141,
    // CMYK 90.84 / 92.34 / 7.32 / 1.01.
    purple: '#413B8D',
    // Darkened primary. Dark-card hover on the marketing pages.
    purpleDeep: '#2A2559',
    // MUI's primary.light off primary.main. Borders and secondary accents.
    purpleMid: '#675FA4',
    purpleSoft: '#B8B1DD',
    purpleTint: '#F5F3FB',
    // Menus and rails that sit ON the purple shell and must lift off it —
    // the editor's dropdowns, the reader's sidebar.
    purpleRaised: '#5A52A3',
    // Pricing's dark plum. Not a mistake and not a shade of `purple`: it is
    // the ground for every large dark surface on that page, chosen because
    // #413B8D is too saturated to sit behind a full page of text. `plumInk`
    // is its darkened partner, used for accent numerals only.
    plum: '#221E38',
    plumInk: '#15132A',

    // The brand gold is a GRADIENT, not a flat colour. `goldFlat` is for
    // borders, chips, small text and anywhere a gradient would read as mud.
    //
    // These five stops are what the product ships today. The client's swatch
    // appears to carry more; when the exact values arrive they are a one-line
    // change here, which is the entire reason this file exists.
    gold: 'linear-gradient(90deg, #D4AF37 0%, #F5D76E 25%, #C49A00 50%, #F5D76E 75%, #D4AF37 100%)',
    goldFlat: '#D4AF37',
    goldLight: '#F5D76E',
    goldDeep: '#C49A00',
    // Gold is unreadable as body text on white; this is the ink to pair with it.
    goldInk: '#6B4F14',

    ink: '#1A1A1A',
    muted: '#6B6B6B',
    line: '#DAD6EA',
    page: '#F5F5F5',
    card: '#FFFFFF',

    // Text and hairlines ON a dark purple ground. The alpha on `onDark` is
    // floored at 0.72 deliberately: the original mocks used 0.45–0.68, which
    // fails AA against this purple. Anything fainter is decorative only.
    onDark: 'rgba(255, 255, 255, 0.72)',
    lineOnDark: 'rgba(255, 255, 255, 0.14)',

    // Semantic. One value each, picked from the several already in use —
    // error was #D32F2F / #EF4444 / #FF4444 / #DC2626 / #F44336 before this.
    // Separate from the brand accent on purpose: gold must never mean "ok".
    success: '#2E7D4F',
    warning: '#B06E00',
    error: '#C62828',
    info: '#1B6FA8',
    // The same four, lightened to stay legible on a dark purple ground.
    // A single error colour cannot serve both: #C62828 on #413B8D is 1.5:1.
    successOnDark: '#7FD4A3',
    warningOnDark: '#F0C260',
    errorOnDark: '#FF9B94',
    infoOnDark: '#8FD3F4',

    // The same four as grounds, for a chip or a pill that has to read as a
    // SHAPE and not just as coloured text.
    //
    // Added because the Manuscripts tab had no ground to use and reached for
    // `page` (#F5F5F5), which against a white card is about 1.08:1 -- the ink
    // read, the pill did not, and every status chip on the tab was really
    // just floating text. Nothing above could have been used instead: the
    // palette had four semantic INKS and no partners for them.
    //
    // Not invented values: each is its ink mixed with white at 10%, which is
    // the construction `purpleTint` already has against `purple` (~5%) and
    // the one every `category` bg pair uses. 10 rather than 5 because 5 is
    // what produced the invisible pill in the first place.
    // `purpleTint` is the 5% ground and is already spent as a SURFACE -- the
    // Manuscripts page shell and its detail panes. A chip needs the same 10%
    // as the four below or it stays shapeless beside them, so the brand gets
    // both strengths rather than one doing two jobs.
    purpleTintDeep: '#ECEBF4',

    infoTint: '#E8F1F6',
    warningTint: '#F7F0E6',
    successTint: '#EBF2ED',
    errorTint: '#F9EAEA',
  },

  /**
   * Editorial category accents, used by the community index.
   *
   * These are content taxonomy rather than UI colour — each pairs an ink with
   * the tint it sits on. They differ in hue AND lightness so they survive
   * being read in greyscale.
   */
  category: {
    writing: { ink: '#413B8D', bg: '#F1EFF9' },
    reading: { ink: '#6B4F14', bg: '#F8F2E4' },
    collaboration: { ink: '#2F6B70', bg: '#E8F1F2' },
    bookKnowHow: { ink: '#8C3F5B', bg: '#F8EDF1' },
    publishing: { ink: '#4A6B45', bg: '#ECF2EB' },
  },

  /**
   * Chart series colours — the first chart in the app, the royalty flow on /quote.
   *
   * They sit on a DARK card (purpleDeep into plum), so each is a mid-light step:
   * the brand purple and gold for ReVisionari and the author, then the `category`
   * hues above, lightened to read on dark, for collaborators. The two greys are
   * for slices that are not people (print cost, an unassigned share).
   *
   * The eight hued values were checked with a palette validator against
   * purpleDeep, not by eye: all inside the dark-mode lightness band, all
   * saturated enough not to read as grey, at least 3:1 against the card, and
   * neighbours in the order below stay apart under red-green colour blindness.
   * That order is the order they are assigned in — keep it if you change one.
   * Not status colours: none of these means good or bad.
   */
  chart: {
    print: '#A8A5B8',
    platform: '#7973CA',
    author: '#B68B16',
    teal: '#18A7A1',
    terracotta: '#B66946',
    orchid: '#B07BBC',
    sage: '#638F49',
    sky: '#5399D1',
    rose: '#B36383',
    unassigned: '#7E7B91',
  },

  /** Page frame. The marketing pages share one content width and gutter. */
  layout: {
    contentMax: 1180,
    wrapPad: 32,
    wrapPadNarrow: 20, // at and below the `sm` breakpoint
  },

  /**
   * 7 steps, down from 38 rendered sizes and 82 size/line-height pairs.
   *
   * `label` and `micro` are the two additions to the original five, and they
   * exist because the scale could not describe a dense component. Below
   * `caption` there was nothing, so the Manuscripts tab floored its card
   * titles, pills, dates, metadata and figures all at 13px -- five tiers of
   * the design collapsed onto one, and the cards came out visibly looser than
   * the mock they were built from.
   *
   * `label` is sans because it is for words; `micro` is mono because it is for
   * figures that line up in a column -- word counts, percentages, dates, the
   * day names in a calendar. 11px is the floor on purpose: the mock went to 9
   * and 10 in places, and that is too small to ship.
   */
  text: {
    display: { size: 56, weight: 600, lineHeight: 1.1, family: family.serif },
    title: { size: 40, weight: 600, lineHeight: 1.15, family: family.serif },
    heading: { size: 28, weight: 600, lineHeight: 1.25, family: family.serif },
    body: { size: 16, weight: 400, lineHeight: 1.55, family: family.sans },
    caption: { size: 13, weight: 400, lineHeight: 1.45, family: family.mono },
    label: { size: 12, weight: 600, lineHeight: 1.4, family: family.sans },
    micro: { size: 11, weight: 600, lineHeight: 1.4, family: family.mono },
  },

  /** 6 steps, down from 50. A 4px grid — every value is a multiple of 4. */
  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 },

  /**
   * 6 values, down from 38.
   *
   * `card` and `panelLg` fill the gap between `panel` (8) and `pill`. The jump
   * from 8 straight to a pill meant anything the mocks drew at 12, 16 or 20
   * landed on 8, which is why the Manuscripts cards and section panels came
   * out squarer than the design they were built from. 12 for a card, 16 for
   * the panel a group of them sits in, so nesting still reads as nesting.
   */
  radius: { none: 0, base: 4, panel: 8, card: 12, panelLg: 16, pill: 999 },

  /** 3 values, down from 107. Spend them by role, not by habit. */
  shadow: {
    // A card lifted off the page.
    sm: '0 1px 3px rgba(26, 26, 26, 0.08)',
    // A menu or popover.
    md: '0 8px 24px rgba(26, 26, 26, 0.14)',
    // A dialog.
    lg: '0 24px 60px rgba(26, 26, 26, 0.25)',
  },

  /** 3 durations and 2 curves, down from 41 durations and 7 curves. */
  motion: {
    fast: 150, // hover, focus, small state changes
    base: 200, // menus, accordions, anything that moves a short distance
    slow: 300, // dialogs, drawers, anything entering from off-screen
    // Standard easing for things entering and settling.
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    // For things leaving, which should not linger.
    exit: 'cubic-bezier(0.4, 0, 1, 1)',
  },

  /**
   * 5 layers, down from 28 values. Named so nobody has to guess whether 9999
   * beats 2000.
   *
   * These match MUI's own ladder deliberately, so themed components and hand
   * written CSS cannot end up on opposite sides of each other. Two things are
   * currently above `toast` and should come down to it in Phase 5: the reader's
   * zoom bar at 2000, and the toast container's own 10000.
   */
  z: { base: 0, raised: 10, sticky: 100, overlay: 1300, toast: 1400 },

  /**
   * 4 breakpoints, down from 24 in the stylesheets.
   *
   * These are MUI's defaults, chosen because the CSS already half-agrees with
   * them — 599/600, 900/901 and 1199/1200 are all in use. The orphans to
   * retire in Phase 5 are 640 and 768 (another framework's numbers) and 820,
   * 950, 980 and 1000, which came from nowhere.
   */
  breakpoint: { sm: 600, md: 900, lg: 1200 },

  family,
};

/**
 * Phase 2: the same tokens, in the one form a plain stylesheet can read.
 *
 * MUI's theme object is only visible to MUI components. The 40 hand-written
 * stylesheets in src/styles/ cannot see it, which is the whole reason five of
 * them keep a private copy of the same colours. Emitting the tokens as CSS
 * custom properties closes that gap: after this,
 *
 *     sx={{ color: 'surface.ink' }}        // a component
 *     color: var(--rv-color-ink);          // a stylesheet
 *
 * resolve to the same value from the same file.
 *
 * One limitation worth knowing: custom properties do NOT work inside a
 * `@media` query condition. `--rv-breakpoint-md` is emitted for reference, but
 * `@media (min-width: var(--rv-breakpoint-md))` is invalid CSS and silently
 * does nothing. Media queries keep their literal numbers until Phase 5 moves
 * them to a build-time solution.
 *
 * Returns a flat object of property names to values, ready to hand to
 * GlobalStyles under `:root`.
 */
export function cssVariables(t = tokens) {
  const vars = {};
  const px = (n) => `${n}px`;

  for (const [key, value] of Object.entries(t.color)) {
    vars[`--rv-color-${kebab(key)}`] = value;
  }
  for (const [key, value] of Object.entries(t.text)) {
    const name = kebab(key);
    vars[`--rv-text-${name}-size`] = px(value.size);
    vars[`--rv-text-${name}-weight`] = String(value.weight);
    vars[`--rv-text-${name}-line-height`] = String(value.lineHeight);
    vars[`--rv-text-${name}-family`] = value.family;
  }
  for (const [key, value] of Object.entries(t.category)) {
    vars[`--rv-category-${kebab(key)}`] = value.ink;
    vars[`--rv-category-${kebab(key)}-bg`] = value.bg;
  }
  for (const [key, value] of Object.entries(t.chart)) {
    vars[`--rv-chart-${kebab(key)}`] = value;
  }
  for (const [key, value] of Object.entries(t.layout)) {
    vars[`--rv-layout-${kebab(key)}`] = px(value);
  }
  // `kebab` on every loop, not just the first four.
  //
  // These seven interpolated the key raw. It went unnoticed for two phases
  // because every key in them was a single lowercase word, so kebab would
  // have been the identity function anyway. The first camelCase key added to
  // any of them -- `radius.panelLg` -- emitted `--rv-radius-panelLg` while
  // the stylesheet asked for `--rv-radius-panel-lg`, and custom property
  // names are case-sensitive. An unresolved var is not an error: the
  // declaration is simply dropped at computed-value time, so `border-radius`
  // fell back to its initial 0 and three panels went square with nothing
  // logged anywhere.
  //
  // This renames no existing property -- kebab is still the identity on every
  // key that exists today -- so it is safe for the 40 stylesheets reading
  // them.
  for (const [key, value] of Object.entries(t.space)) {
    vars[`--rv-space-${kebab(key)}`] = px(value);
  }
  for (const [key, value] of Object.entries(t.radius)) {
    vars[`--rv-radius-${kebab(key)}`] = px(value);
  }
  for (const [key, value] of Object.entries(t.shadow)) {
    vars[`--rv-shadow-${kebab(key)}`] = value;
  }
  for (const [key, value] of Object.entries(t.motion)) {
    vars[`--rv-motion-${kebab(key)}`] =
      typeof value === 'number' ? `${value}ms` : value;
  }
  for (const [key, value] of Object.entries(t.z)) {
    vars[`--rv-z-${kebab(key)}`] = String(value);
  }
  for (const [key, value] of Object.entries(t.breakpoint)) {
    vars[`--rv-breakpoint-${kebab(key)}`] = px(value);
  }
  for (const [key, value] of Object.entries(t.family)) {
    vars[`--rv-family-${kebab(key)}`] = value;
  }

  return vars;
}

/** goldFlat -> gold-flat, so the CSS names read like CSS. */
function kebab(s) {
  return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

const theme = createTheme({
  palette: {
    primary: {
      main: tokens.color.purple,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.color.goldFlat,
      contrastText: tokens.color.ink,
    },
    // Surfaces for the signed-in app shell (the .dashboard-layout pages).
    // Live: HomeSignedIn.jsx, AnalyticsPanel.jsx and ActivityMarquee.jsx read
    // these, so the values are left exactly as they were.
    //
    // Two of them are near-misses of the canonical tokens above — `tile`
    // #F8F6FB against `purpleTint` #F5F3FB, and `border` #DDD5EA against
    // `line` #DAD6EA. They differ by 3 to 6 points and no one can see the
    // difference, but they are two values where there should be one.
    // Reconciling them moves pixels, so it belongs to Phase 3 alongside
    // collapsing the five stylesheet copies — not here.
    surface: {
      page: '#F5F5F5',       // .dashboard-layout / .dashboard-main background
      card: '#FFFFFF',       // .section
      tile: '#F8F6FB',       // stat tiles
      border: '#DDD5EA',
      ink: '#1A1A1A',
      muted: '#6B6B6B',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          /**
           * Phase 4: the disabled state, fixed once for every button.
           *
           * MUI's default disabled style is `rgba(0,0,0,.26)` ink on
           * `rgba(0,0,0,.12)` fill. Both are translucent BLACK, which assumes
           * a light background. Dropped on the purple new-document dialog it
           * renders at 1.3:1 — the audit found the primary action was
           * effectively invisible there, and 1.4:1 on Admin's white cards.
           *
           * Worse, it wins on specificity: a button can set its own gold
           * background in `sx` and still be repainted by `.Mui-disabled`.
           *
           * Fading the whole control instead keeps the button's own colours,
           * so it reads as inactive on any ground and its internal contrast
           * is preserved rather than replaced. 0.6 is close to the 0.65 the
           * quote page already uses, so this matches an idiom the product
           * had rather than inventing one.
           *
           * Only `opacity` belongs here. An earlier attempt also set
           * `backgroundColor: 'inherit'`, which does not mean "leave it
           * alone" — it means "take the parent's", so a disabled button
           * inside the purple dialog turned purple. The colours have to be
           * restored per variant, below, where the real values are known.
           */
          '&.Mui-disabled': {
            opacity: 0.6,
          },

          /**
           * The touch minimum, applied once instead of 1,844 times.
           *
           * The audit found exactly one button on the whole public site that
           * cleared 44px. Most sat at 33–41, and the price-breakdown caret was
           * 8×9. `minHeight` rather than `height` so a button with wrapped
           * label text still grows.
           */
          minHeight: 44,
          borderRadius: `${tokens.radius.base}px`,

          /**
           * The focus ring.
           *
           * The sharpest number in the audit: 112 keyboard stops across seven
           * pages, 8 of them with focus styling this codebase wrote. Of the
           * rest, 59 fell back to the browser's default ring — which takes its
           * colour from `color`, so it drew near-black hairlines on the purple
           * sidebar — and 39 showed only MUI's edgeless ripple.
           *
           * `:focus-visible` so it appears for keyboard users and not on
           * click. Two rings, gold over purple, so one of them always has
           * contrast whichever ground the button sits on.
           */
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${tokens.color.goldFlat}, 0 0 0 4px ${tokens.color.purple}`,
          },
        },
        containedSecondary: {
          background: tokens.color.gold,
          color: tokens.color.ink,
          '&:hover': { filter: 'brightness(0.95)' },
          // Beat MUI's translucent-black disabled pair with the button's own
          // colours, so only the opacity above communicates the state.
          '&.Mui-disabled': {
            background: tokens.color.gold,
            color: tokens.color.ink,
          },
        },
        containedPrimary: {
          '&.Mui-disabled': {
            backgroundColor: tokens.color.purple,
            color: '#FFFFFF',
          },
        },
        outlinedSecondary: {
          '&.Mui-disabled': {
            borderColor: tokens.color.goldFlat,
            color: tokens.color.goldFlat,
          },
        },
        outlinedPrimary: {
          '&.Mui-disabled': {
            borderColor: tokens.color.purple,
            color: tokens.color.purple,
          },
        },
      },
    },

    /**
     * The same focus ring for the other two things people tab to.
     *
     * Icon buttons are most of the editor's toolbar, and the audit found two
     * of them rendering no focus indicator at all — including a Delete page
     * button that is invisible until hover, so keyboard focus landed on a
     * control nobody could see.
     */
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${tokens.color.goldFlat}, 0 0 0 4px ${tokens.color.purple}`,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.goldFlat}`,
            outlineOffset: 2,
            borderRadius: `${tokens.radius.base}px`,
          },
        },
      },
    },
  },
});

export default theme;
